import React, { Suspense } from "react";
import { Box, ThemeProvider } from "@mui/material";
import { loginRequest, requestPasswordReset } from "./backend/taxguru/client/jmf/coreAuthClient";
import Navigation from "./navigation/Navigation";
import Container from "@mui/material/Container";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { changePassword } from "./backend/taxguru/client/jmf/coreAccountClient";
import { lightThemeOptions } from "./TaxGuruStyles";
import { createTheme } from "@mui/material/styles";
import { csCZ as coreCsCZ, enUS as coreEnUS } from "@mui/material/locale";
import { csCZ as pickersCsCZ, enUS as pickersEnUS } from "@mui/x-date-pickers/locales";
import CssBaseline from "@mui/material/CssBaseline";
import useInitialiseApplication from "./hook/useInitialiseApplication";
import { QueryClient, QueryClientProvider } from "react-query";
import { useTranslation } from "react-i18next";
import { Outlet } from "react-router-dom";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import relativeTime from "dayjs/plugin/relativeTime";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import Image from "mui-image";
import FeedbackDialog from "./modules/feedback/component/FeedbackDialog";
import WelcomeModal from "./components/WelcomeModal";
import OnboardingModal from "./modules/accountant/component/OnboardingModal";
import MaicaChatbot from "./components/MaicaChatbot";
import RegistrationLink from "./modules/registration/selfregistration/component/RegistrationLink";
import { JmfAuthenticated } from "@mm-products/java-multima-framework";
import TaxGuruCodeSplittingErrorBoundary from "./navigation/error/TaxGuruCodeSplittingErrorBoundary";

dayjs.extend(isBetween);
dayjs.extend(relativeTime);
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

export const TaxLogoFallback = ({ variant }: { variant?: "businessCard" }) => {
    const imageHeight = variant === "businessCard" ? "50px" : "10rem";
    return (
        <Box paddingTop={variant === "businessCard" ? 0 : 5} width="100%" height="100%" display="flex" justifyContent="center" alignItems="center">
            <Box
                sx={{
                    animation: "coin-rotate 2s infinite",
                }}
            >
                <Image height={imageHeight} duration={0} src={"/symbol.svg"} />
            </Box>
        </Box>
    );
};

export const queryClient = new QueryClient();
const TaxGuru = () => {
    const { username, initialised, forceInitialisation } = useInitialiseApplication();
    const { i18n } = useTranslation();
    const twoLetterLang = i18n.language.split("-")[0];
    React.useEffect(() => {
        console.debug("Requiring language", i18n.language, " as result code ", twoLetterLang);
        console.debug("Available languages are", i18n.languages);
        const importLanguage = async () => {
            try {
                await import(`dayjs/locale/${twoLetterLang}.js`).then(() => {
                    dayjs.locale(twoLetterLang);
                });
            } catch (reason) {
                console.error("Failed to set locale to", twoLetterLang, reason);
                await import("dayjs/locale/cs.js").then(() => {
                    dayjs.locale("cs");
                });
            }
        };
        importLanguage();
    }, [twoLetterLang, i18n.language, i18n.languages]);
    // MUI a MUI X pickers v jazyce aplikace (aria-label a texty Autocomplete, date pickerů, stránkování...)
    const isEnglish = twoLetterLang === "en";
    const theme = React.useMemo(
        // ThemeOptions, ne hotový Theme - jinak je typová kontrola extrémně drahá (dev server spadne na nedostatek paměti)
        () => createTheme(lightThemeOptions, isEnglish ? coreEnUS : coreCsCZ, isEnglish ? pickersEnUS : pickersCsCZ),
        [isEnglish]
    );
    return (
        <ThemeProvider theme={theme}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={twoLetterLang}>
                <QueryClientProvider client={queryClient}>
                    <JmfAuthenticated
                        passwordResetRequest={requestPasswordReset}
                        logoDef={{
                            logo: <Image duration={250} fit="contain" height="70px" src="/logo.svg" />,
                        }}
                        passwordChangeRequest={changePassword}
                        login={loginRequest}
                        onLogin={forceInitialisation}
                        authenticated={initialised ? username !== undefined : undefined}
                        footerElement={<RegistrationLink />}
                    >
                        <FeedbackDialog />
                        <OnboardingModal />
                        <WelcomeModal />
                        <MaicaChatbot />
                        <CssBaseline />
                        <Navigation />
                        <Box sx={{ paddingBottom: 5 }} />
                        <Container maxWidth="xl" component="main">
                            <TaxGuruCodeSplittingErrorBoundary>
                                <Suspense fallback={<TaxLogoFallback />}>
                                    <Outlet />
                                </Suspense>
                            </TaxGuruCodeSplittingErrorBoundary>
                        </Container>
                        <Box id="footer" sx={{ paddingBottom: 5 }} />
                    </JmfAuthenticated>
                </QueryClientProvider>
            </LocalizationProvider>
        </ThemeProvider>
    );
};

export default TaxGuru;
